import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Candidates|Summary|Affiliations';
const sharedData: Pick<LocDef, 'feature' | 'type' | 'portal' | 'htmlAllowed' | 'shared'> = {
	feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
	portal: 'SP',
	htmlAllowed: false,
	shared: false,
};

export type SpCandidateAffiliationLocs =
	| 'SPCandidateAffiliationType'
	| 'SPCandidateAffiliationTypeRequired'
	| 'SPCandidateAffiliationTypeInvalid'
	| 'SPCandidateAffiliationName'
	| 'SPCandidateAffiliationNameRequired'
	| 'SPCandidateAffiliationNameInvalid'
	| 'SPCandidateAffiliationEmail'
	| 'SPCandidateAffiliationEmailRequired'
	| 'SPCandidateAffiliationEmailInvalid'
	| 'SPCandidateAffiliationEmailUnsupported'
	| 'SPCandidateAffiliationId'
	| 'SPCandidateAffiliationIdRequired'
	| 'SPCandidateAffiliationIdInvalid'
	| 'SPCandidateAffiliationOtherName'
	| 'SPCandidateAffiliationOtherNameRequired'
	| 'SPCandidateAffiliationOtherNameInvalid'
	| 'SPCandidateAffiliationUpdate'
	| 'SPCandidateAffiliationSaveNotice';

export type SpCandidateAffLocsType = LocMap<SpCandidateAffiliationLocs>;

export const spSharedCandidateAffiliationLocs: LocDefs<SpCandidateAffiliationLocs> = {
	SPCandidateAffiliationUpdate: {
		...sharedData,
		defaultValue: 'Update',
		description: 'The update button on the form and the update icon in the section header.',
		displayType: 'Button',
		displayPath: `${dpRoot}|Update`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.Button.Update',
	},
	SPCandidateAffiliationNameInvalid: {
		...sharedData,
		defaultValue: 'Affiliation name is invalid',
		description: 'Error saying the affiliation name is invalid.',
		displayType: 'Error',
		displayPath: `${dpRoot}|Name Invalid`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldError.AffNameInvalid',
	},
	SPCandidateAffiliationNameRequired: {
		...sharedData,
		defaultValue: 'Affiliation name is required',
		description: 'Error saying the affiliation name is required.',
		displayType: 'Error',
		displayPath: `${dpRoot}|Name Required`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldError.AffNameRequired',
	},
	SPCandidateAffiliationTypeInvalid: {
		...sharedData,
		defaultValue: 'Affiliation type is invalid',
		description: 'Error saying the affiliation type is invalid.',
		displayType: 'Error',
		displayPath: `${dpRoot}|Type Invalid`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldError.AffTypeInvalid',
	},
	SPCandidateAffiliationTypeRequired: {
		...sharedData,
		defaultValue: 'Affiliation type is required',
		description: 'Error saying the affiliation type is required.',
		displayType: 'Error',
		displayPath: `${dpRoot}|Type Required`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldError.AffTypeRequired',
	},
	SPCandidateAffiliationEmailRequired: {
		...sharedData,
		defaultValue: 'Affiliation email is required',
		description: 'Error saying the affiliation email is required.',
		displayType: 'Error',
		displayPath: `${dpRoot}|Email Required`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldError.EmailRequired',
	},
	SPCandidateAffiliationIdInvalid: {
		...sharedData,
		defaultValue: 'Affiliation ID is invalid',
		description: 'Error saying the affiliation ID is invalid.',
		displayType: 'Error',
		displayPath: `${dpRoot}|ID Invalid`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldError.IdInvalid',
	},
	SPCandidateAffiliationIdRequired: {
		...sharedData,
		defaultValue: 'Affiliation ID is required',
		description: 'Error saying the affiliation ID is required.',
		displayType: 'Error',
		displayPath: `${dpRoot}|ID Required`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldError.IdRequired',
	},
	SPCandidateAffiliationEmailInvalid: {
		...sharedData,
		defaultValue: 'Invalid email address',
		description: 'Error saying the affiliation email is invalid.',
		displayType: 'Error',
		displayPath: `${dpRoot}|Email Invalid`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldError.InvalidEmail',
	},
	SPCandidateAffiliationOtherNameInvalid: {
		...sharedData,
		defaultValue: 'Other company name is invalid',
		description: 'Error saying the other company name is invalid.',
		displayType: 'Error',
		displayPath: `${dpRoot}|Other Name Invalid`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldError.OtherNameInvalid',
	},
	SPCandidateAffiliationOtherNameRequired: {
		...sharedData,
		defaultValue: 'Other company name is required',
		description: 'Error saying the other company name is required.',
		displayType: 'Error',
		displayPath: `${dpRoot}|Other Name Required`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldError.OtherNameRequired',
	},
	SPCandidateAffiliationEmailUnsupported: {
		...sharedData,
		defaultValue: 'Unsupported email address',
		description: 'Error saying the affiliation email is unsupported.',
		displayType: 'Error',
		displayPath: `${dpRoot}|Email Unsupported`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldError.UnsupportedEmail',
	},
	SPCandidateAffiliationEmail: {
		...sharedData,
		defaultValue: 'Affiliation Email',
		description: 'Label for the affiliation email. This is used in the form and the display.',
		displayType: 'Label',
		displayPath: `${dpRoot}|Email`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldLabel.Email',
	},
	SPCandidateAffiliationId: {
		...sharedData,
		defaultValue: 'Affiliation ID',
		description: 'Label for the affiliation id. This is used in the form and the display.',
		displayType: 'Label',
		displayPath: `${dpRoot}|ID`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldLabel.Id',
	},
	SPCandidateAffiliationName: {
		...sharedData,
		defaultValue: 'Affiliation Name',
		description: 'Label for the affiliation name section.',
		displayType: 'Label',
		displayPath: `${dpRoot}|Name`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldLabel.Name',
	},
	SPCandidateAffiliationOtherName: {
		...sharedData,
		defaultValue: 'Other Company Name',
		description: 'Label for the other company name. This is used in the form and the display.',
		displayType: 'Label',
		displayPath: `${dpRoot}|Other Name`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldLabel.OtherName',
	},
	SPCandidateAffiliationType: {
		...sharedData,
		defaultValue: 'Affiliation Type',
		description: 'Label for the affiliation type section.',
		displayType: 'Label',
		displayPath: `${dpRoot}|Type`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.FieldLabel.Type',
	},
	SPCandidateAffiliationSaveNotice: {
		...sharedData,
		defaultValue: 'Affiliations changes saved',
		description: 'The message shown when the affiliations update is successful.',
		displayType: 'Notification',
		displayPath: `${dpRoot}|Save`,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Candidates.Affiliations.Notification.ChangesSaved',
	},
};

export const SpCandidateAffLocs = MapLocKeys<SpCandidateAffiliationLocs>(
	spSharedCandidateAffiliationLocs
);
